import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchModules, updateModulePrice, updateModuleStatus } from "../store/Module/moduleSlice";
import { setLoading } from "../store/Loading/loadingSlice";

const AdminModuleManager = () => {
  const dispatch = useDispatch();
  const { modules, status, error } = useSelector((state) => state.modules);
  const [priceInputs, setPriceInputs] = useState({});
  const [intervalInputs, setIntervalInputs] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [enabledModules, setEnabledModules] = useState({});
  const [activeStripeModal, setActiveStripeModal] = useState(null);

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(fetchModules()).then(() => dispatch(setLoading(false)));
  }, [dispatch]);

  // Initialize form inputs with module data
  useEffect(() => {
    if (modules.length > 0) {
      const initialPrices = {};
      const initialIntervals = {};
      const initialEnabledStatus = {};
      modules.forEach(module => {
        initialPrices[module.cat] = module.pricePerMonth;
        initialIntervals[module.cat] = module.stripeInterval || "month";
        initialEnabledStatus[module.cat] = module.status || false;
      });
      setPriceInputs(initialPrices);
      setIntervalInputs(initialIntervals);
      setEnabledModules(initialEnabledStatus);
    }
  }, [modules]);

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const handlePriceChange = (cat, value, autoUpdate = false) => {
    setPriceInputs((prev) => ({ ...prev, [cat]: value }));

    if (autoUpdate) {
      const price = parseFloat(value);
      if (price && price >= 0) {
        dispatch(updateModulePrice({ 
          cat, 
          pricePerMonth: price,
          interval: intervalInputs[cat] || "month"
        }))
          .unwrap()
          .then(() => {
            console.log(`Price auto-updated for cat ${cat}`);
          })
          .catch(() => {
            console.error("Auto price update failed");
          });
      }
    }
  };

  const handleIntervalChange = (cat, value) => {
    setIntervalInputs((prev) => ({ ...prev, [cat]: value }));
  };

  const handleSavePrice = (module) => {
    const price = parseFloat(priceInputs[module.cat]);
    const interval = intervalInputs[module.cat] || module.stripeInterval || "month";
    
    if (!price || price < 0) {
      alert("Please enter a valid price");
      return;
    }

    dispatch(updateModulePrice({ 
      cat: module.cat, 
      pricePerMonth: price,
      interval
    }))
      .unwrap()
      .then(() => {
        alert(`Price updated for module "${module.title}"`);
        setActiveStripeModal(null);
      })
      .catch(() => {
        alert("Failed to update price");
      });
  };

  const handleToggleStatus = (cat, currentStatus) => {
    const newStatus = !currentStatus;
    dispatch(updateModuleStatus({ cat, status: newStatus }))
      .unwrap()
      .then(() => {
        setEnabledModules(prev => ({ ...prev, [cat]: newStatus }));
      })
      .catch(() => {
        alert("Failed to update module status");
      });
  };

  return (
    <div className="row">
      <div className="col-lg-12 col-md-12 col-sm-12 p-4">
        <div className="row rounded">
          <div className="col-md-12 p-0 m-0">
            <h4 className="card-title text-left mb-3">Module Management</h4>

            <table className="custom-table w-full divide-y divide-gray-200 revenueTable">
              <thead>
                <tr>
                  <th className="px-4 py-2">
                    <input type="checkbox" />
                  </th>
                  <th className="px-4 py-2">Module Name</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Monthly Price ($)</th>
                  <th className="px-4 py-2">Plan Status</th>
                  <th className="px-4 py-2">Subscribers</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {status === "loading" && (
                  <tr>
                    <td className="px-4 py-2 text-center" colSpan="7">
                      Loading...
                    </td>
                  </tr>
                )}
                {status === "failed" && (
                  <tr>
                    <td className="px-4 py-2 text-center" colSpan="7">
                      Error: {error}
                    </td>
                  </tr>
                )}
                {modules.length > 0 ? (
                  modules.map((mod, index) => (
                    <tr key={mod.cat} className="hover:bg-gray-50 h-auto">
                      <td className="px-4 py-3 align-top">
                        <input type="checkbox" />
                      </td>
                      <td className="px-4 py-3 align-top">{mod.title}</td>
                      <td className="px-4 py-3 align-top">{mod.cat}</td>
                      <td className="px-4 py-3 align-top">
                        <input
                          type="number"
                          className="form-control w-50"
                          placeholder="$0.00"
                          value={priceInputs[mod.cat] ?? mod.pricePerMonth ?? ""}
                          onChange={(e) =>
                            handlePriceChange(mod.cat, e.target.value, true)
                          }
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <label className="form-check form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={enabledModules[mod.cat] || false}
                            onChange={() => handleToggleStatus(mod.cat, enabledModules[mod.cat])}
                          />
                        </label>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {mod.subscribers?.length || 0}
                      </td>
                      <td className="px-4 py-3 align-top d-flex position-relative">
                        <button
                          className="actions-button px-4"
                          style={{ background: "#f7f7f7" }}
                          onClick={() => toggleDropdown(index)}
                        >
                          Actions{" "}
                          <svg
                            className="arrow-icon"
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                          >
                            <path d="M7 10l5 5 5-5z" />
                          </svg>
                        </button>
                        <div
                          className={`dropdown-menu rounded-0 ${
                            openDropdown === index ? "d-block" : ""
                          } actionDropDown`}
                          style={{ background: "#f7f7f7" }}
                        >
                          <div className="row">
                            <div className="col-sm-12">
                              <li onClick={() => setActiveStripeModal(mod)}>
                                Update
                              </li>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-2 text-center" colSpan="7">
                      No Modules Found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {activeStripeModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-credit-card me-2"></i>
                  Stripe Plan Details - {activeStripeModal.title}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setActiveStripeModal(null)}
                />
              </div>
              <div className="modal-body">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card mb-3">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Plan Information</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label fw-bold">Category</label>
                          <p>{activeStripeModal.cat}</p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-bold">Stripe Product ID</label>
                          <p className="text-truncate">
                            {activeStripeModal.stripeProductId || "N/A"}
                          </p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-bold">Stripe Price ID</label>
                          <p className="text-truncate">
                            {activeStripeModal.stripePriceId || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Pricing Configuration</h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label fw-bold">Price Amount</label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                              type="number"
                              className="form-control"
                              value={
                                priceInputs[activeStripeModal.cat] ??
                                activeStripeModal.pricePerMonth
                              }
                              onChange={(e) =>
                                handlePriceChange(activeStripeModal.cat, e.target.value)
                              }
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-bold">Billing Interval</label>
                          <select
                            className="form-select"
                            value={
                              intervalInputs[activeStripeModal.cat] ??
                              activeStripeModal.stripeInterval ??
                              "month"
                            }
                            onChange={(e) =>
                              handleIntervalChange(activeStripeModal.cat, e.target.value)
                            }
                          >
                            <option value="day">Daily</option>
                            <option value="week">Weekly</option>
                            <option value="month">Monthly</option>
                            <option value="year">Yearly</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Subscribers ({activeStripeModal.subscribers?.length || 0})</h6>
                  </div>
                  <div className="card-body">
                    {activeStripeModal.subscribers?.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Subscription Date</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeStripeModal.subscribers.map((s, idx) => (
                              <tr key={idx}>
                                <td>
                                  {s.userId?.name || s.userId?.email || s.userId}
                                </td>
                                <td>
                                  {new Date(s.subscribedAt).toLocaleDateString()}
                                </td>
                                <td>
                                  <span className="badge bg-success">Active</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="alert alert-info mb-0">
                        No subscribers found for this plan.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setActiveStripeModal(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => handleSavePrice(activeStripeModal)}
                >
                  <i className="fas fa-save me-2"></i>
                  Update Plan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal backdrop */}
      {activeStripeModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default AdminModuleManager;